import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import {
  getStoredConsent,
  initialiseIframeConsent,
  isIframeMode,
  storeConsent
} from "../services/cookieConsent/cookieConsentService";
import { CookieConsentState } from "../services/cookieConsent/types";
import { isBrowser } from "../utils/environment";

interface CookieConsentContextValue {
  consent: CookieConsentState;
  showBanner: boolean;
  canManagePreferences: boolean;
  acceptAll: () => void;
  rejectNonEssential: () => void;
  updateConsent: (analytics: boolean, functional: boolean) => void;
  reopenBanner: () => void;
}

const defaultConsent: CookieConsentState = {
  isResolved: false,
  analytics: false,
  functional: false
};

const CookieConsentContext = createContext<CookieConsentContextValue>({
  consent: defaultConsent,
  showBanner: false,
  canManagePreferences: false,
  acceptAll: () => {},
  rejectNonEssential: () => {},
  updateConsent: () => {},
  reopenBanner: () => {}
});

export function CookieConsentProvider({
  children
}: Readonly<{
  children: React.ReactNode;
}>) {
  const [consent, setConsent] = useState<CookieConsentState>(defaultConsent);
  const [showBanner, setShowBanner] = useState(false);
  const [isBrowserMode, setIsBrowserMode] = useState(false);

  useEffect(() => {
    if (!isBrowser()) {
      setConsent({ isResolved: true, analytics: true, functional: true });
      return;
    }

    if (isIframeMode()) {
      initialiseIframeConsent().then((resolved) => {
        setConsent(resolved);
      });
      return;
    }

    setIsBrowserMode(true);

    const stored = getStoredConsent();
    if (stored) {
      setConsent(stored);
    } else {
      setShowBanner(true);
    }
  }, []);

  const acceptAll = useCallback(() => {
    storeConsent(true, true);
    setConsent({ isResolved: true, analytics: true, functional: true });
    setShowBanner(false);
  }, []);

  const rejectNonEssential = useCallback(() => {
    storeConsent(false, false);
    setConsent({ isResolved: true, analytics: false, functional: false });
    setShowBanner(false);
  }, []);

  const updateConsent = useCallback(
    (analytics: boolean, functional: boolean) => {
      storeConsent(analytics, functional);
      setConsent({ isResolved: true, analytics, functional });
      setShowBanner(false);
    },
    []
  );

  const reopenBanner = useCallback(() => {
    setShowBanner(true);
  }, []);

  const canManagePreferences = isBrowserMode && consent.isResolved;

  const value = useMemo(
    () => ({
      consent,
      showBanner,
      canManagePreferences,
      acceptAll,
      rejectNonEssential,
      updateConsent,
      reopenBanner
    }),
    [
      consent,
      showBanner,
      canManagePreferences,
      acceptAll,
      rejectNonEssential,
      updateConsent,
      reopenBanner
    ]
  );

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent(): CookieConsentContextValue {
  return useContext(CookieConsentContext);
}
